import { useTranslation } from 'react-i18next'
import {
  Autocomplete,
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Rating,
  Select,
  TextField,
  Typography,
} from '@mui/material'
import type {
  StudentFormAnswerValue,
  StudentFormField,
} from '@/api/studentForms'

interface StudentFormFieldInputProps {
  field: StudentFormField
  value: StudentFormAnswerValue
  /** Только чтение: поле student_visible у ученика или анкета в режиме просмотра. */
  readOnly?: boolean
  onChange: (value: StudentFormAnswerValue) => void
}

/**
 * Ввод одного поля анкеты — виджет подбирается по типу поля.
 *
 * Поля только для чтения не «дизейблятся»: приглушённый текст плохо читается,
 * а значение здесь важно видеть. Вместо этого ввод блокируется через
 * `readOnly`, и поле остаётся обычного контраста.
 */
export function StudentFormFieldInput({
  field,
  value,
  readOnly = false,
  onChange,
}: StudentFormFieldInputProps) {
  const { t } = useTranslation()
  const options = field.options ?? []

  // Приглушённая подложка вместо серого текста: поле видно, что не правится,
  // но значение читается так же хорошо, как в обычном поле.
  const readOnlySx = readOnly
    ? { '& .MuiInputBase-root': { bgcolor: 'action.hover' } }
    : undefined

  if (field.type === 'boolean') {
    return (
      <FormControlLabel
        control={
          <Checkbox
            checked={value === true}
            readOnly={readOnly}
            onChange={(e) => !readOnly && onChange(e.target.checked)}
          />
        }
        label={field.label}
      />
    )
  }

  if (field.type === 'rating') {
    return (
      <Box>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {field.label}
          {field.required ? ' *' : ''}
        </Typography>
        <Rating
          value={typeof value === 'number' ? value : null}
          readOnly={readOnly}
          onChange={(_, next) => onChange(next)}
        />
      </Box>
    )
  }

  if (field.type === 'select') {
    const labelId = `student-form-${field.key}`
    return (
      <FormControl size="small" fullWidth sx={readOnlySx}>
        <InputLabel id={labelId}>{field.label}</InputLabel>
        <Select
          labelId={labelId}
          label={field.label}
          readOnly={readOnly}
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}
        >
          <MenuItem value="">—</MenuItem>
          {options.map((option) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    )
  }

  if (field.type === 'multiselect') {
    return (
      <Autocomplete
        multiple
        readOnly={readOnly}
        sx={readOnlySx}
        options={options}
        value={Array.isArray(value) ? value : []}
        onChange={(_, next) => onChange(next)}
        renderInput={(params) => (
          <TextField {...params} size="small" label={field.label} required={field.required} />
        )}
      />
    )
  }

  if (field.type === 'language_pair') {
    const pair =
      value && typeof value === 'object' && !Array.isArray(value)
        ? (value as { from?: string; to?: string })
        : { from: '', to: '' }

    return (
      <Box>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {field.label}
          {field.required ? ' *' : ''}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField
            size="small"
            label={t('studentForms.field.languageFrom')}
            value={pair.from ?? ''}
            sx={readOnlySx}
            InputProps={{ readOnly }}
            onChange={(e) => onChange({ from: e.target.value, to: pair.to ?? '' })}
          />
          <TextField
            size="small"
            label={t('studentForms.field.languageTo')}
            value={pair.to ?? ''}
            sx={readOnlySx}
            InputProps={{ readOnly }}
            onChange={(e) => onChange({ from: pair.from ?? '', to: e.target.value })}
          />
        </Box>
      </Box>
    )
  }

  const inputType =
    field.type === 'date' ? 'date' : field.type === 'email' ? 'email' : field.type === 'url' ? 'url' : 'text'

  return (
    <TextField
      size="small"
      fullWidth
      type={inputType}
      label={field.label}
      required={field.required}
      multiline={field.type === 'textarea'}
      minRows={field.type === 'textarea' ? 3 : undefined}
      value={typeof value === 'string' ? value : ''}
      onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}
      sx={readOnlySx}
      InputProps={{ readOnly }}
      InputLabelProps={field.type === 'date' ? { shrink: true } : undefined}
    />
  )
}
